import { Controller } from "@hotwired/stimulus"
import { AgCharts, AgChartsTheme, LicenseManager } from "ag-charts-enterprise";

LicenseManager.setLicenseKey("Using_this_{AG_Charts_and_AG_Grid}_Enterprise_key_{AG-090595}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{NowSignage}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{NowSignage}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{NowSignage}_need_to_be_licensed___{NowSignage}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Charts_and_AG_Grid}_Enterprise_versions_released_before_{26_June_2026}____[v3]_[0102]_MTc4MjQyODQwMDAwMA==3b13b2ef80749631c8486a224fefaacd");

export default class extends Controller {
  static targets = ["chart"];
  static values = {
    series: { type: Array, default: [] },
    data: { type: Array, default: [] },
    title: { type: Object, default: {} },
    subtitle: { type: Object, default: {} },
    animation: { type: Boolean, default: true },
    palette: { type: Array, default: null },
    extraOptions: { type: Object, default: {} }
  }

  connect() {
    if (this.dataValue.length === 0 || this.seriesValue.length === 0) {
      console.error(`No data or series provided for ${this.element.id} chart`);
      return;
    }

    this.initializeTheme();
    this.createChart(true);
    this.setupThemeObserver();
  }

  createChart(isInitialLoad = false) {
    const myTheme = {
      baseTheme: 'ag-default',
      params: {
        fontFamily: "Geist",
        fontSize: 14,
        fontWeight: 400,
      }
    };

    // Apply custom palette if provided
    if (this.paletteValue && this.paletteValue.length > 0) {
      myTheme.palette = {
        fills: this.paletteValue,
        strokes: this.paletteValue
      };
    }

    // Determine theme mode and apply appropriate base theme
    const themeMode = document.body.getAttribute('data-ag-theme-mode');
    const chartTheme = themeMode === 'dark' ? {
      ...myTheme,
      baseTheme: 'ag-default-dark',
      params: {
        ...myTheme.params,
        foregroundColor: "#ffffff",
        backgroundColor: "#1c1c21",
        tooltipBackgroundColor: "#2a2a31",
        tooltipTextColor: "#ffffff",
      }
    } : {
      ...myTheme,
      baseTheme: 'ag-default',
      params: {
        ...myTheme.params,
        foregroundColor: "#1F2937",
        backgroundColor: "#ffffff",
        tooltipBackgroundColor: "#1F2937",
        tooltipTextColor: "#000000",
      }
    };

    // Resolve any formatter functions passed in the options
    const resolvedSeries = this.resolveFunctions(this.seriesValue);
    const resolvedExtraOptions = this.resolveFunctions(this.extraOptionsValue);

    const options = {
      theme: chartTheme,
      container: this.chartTarget,
      data: this.dataValue,
      series: resolvedSeries,
      title: this.titleValue,
      subtitle: this.subtitleValue,
      animation: {
        enabled: isInitialLoad ? this.animationValue : false
      },
      ...resolvedExtraOptions
    }
    
    if (this.chart) {
      this.chart.destroy();
    }
    
    this.chart = AgCharts.create(options);
  }

  // Recursively find 'formatter' and 'renderer' keys at any level and replace the string values with actual functions
  resolveFunctions(obj) {
    //Add below any custom functions that you want to use to format data for a chart
    const functionMap = {
      'test_percentage_formatter': ({value}) => {
        return value + '%';
      },
      'test_percentage_tooltip_renderer': (params) => {
        const uptimeValue = params.datum[params.colorKey];
        return {
          heading: `${params.datum[params.yKey]} ${params.datum[params.xKey]}`,
          data: [
            {
              label: params.colorName || 'Uptime',
              value: `${uptimeValue}%`
            }
          ]
        };
      }
    };

    if (typeof obj !== 'object' || obj === null) {
      return obj;
    }

    // Preserve array vs object structure
    const resolved = Array.isArray(obj) ? [] : {};

    for (const [key, value] of Object.entries(obj)) {
      if ((key === 'formatter' || key === 'renderer') && typeof value === 'string' && functionMap[value]) {
        // Found a formatter/renderer key with string value - replace with function
        resolved[key] = functionMap[value];
      } else if (typeof value === 'object' && value !== null) {
        // Recursively process nested objects/arrays to find formatters/renderers at deeper levels
        resolved[key] = this.resolveFunctions(value);
      } else {
        // Keep all other primitive values as-is
        resolved[key] = value;
      }
    }

    return resolved;
  }

  initializeTheme() {
    const existingMode = document.body.getAttribute('data-ag-theme-mode');
    
    if (!existingMode) {
      const themeValue = document.body.getAttribute('data-themes-theme-value');

      if (themeValue) {
        document.body.setAttribute('data-ag-theme-mode', themeValue);
      } else {
        const isDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
        const systemTheme = isDarkMode ? 'dark' : 'light';
        document.body.setAttribute('data-ag-theme-mode', systemTheme);
      }
    }
  }

  setupThemeObserver() {
    // Watch for changes to the data-ag-theme-mode attribute on body as AG Charts does not support automatic theme change detection
    this.themeObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-ag-theme-mode') {
          this.createChart(false);
        }
      });
    });

    this.themeObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-ag-theme-mode']
    });
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy();
    }
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
  }
}